import { cn } from "@/lib/utils";
import { Fragment, type ReactNode } from "react";

function renderInline(text: string): ReactNode[] {
  const out: ReactNode[] = [];
  const re =
    /\*\*([^*\n]+)\*\*|__([^_\n]+)__|\*([^*\n]+)\*|_([^_\n]+)_|`([^`\n]+)`|\[([^\]]+)\]\(([^)\s]+)\)/g;
  let last = 0;
  let m: RegExpExecArray | null;
  let key = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) out.push(text.slice(last, m.index));
    if (m[1] || m[2]) {
      out.push(
        <strong key={key++} className="font-semibold text-foreground">
          {m[1] ?? m[2]}
        </strong>,
      );
    } else if (m[3] || m[4]) {
      out.push(
        <em key={key++} className="italic">
          {m[3] ?? m[4]}
        </em>,
      );
    } else if (m[5]) {
      out.push(
        <code
          key={key++}
          className="rounded bg-secondary px-1.5 py-0.5 font-mono text-[0.85em] text-primary"
        >
          {m[5]}
        </code>,
      );
    } else if (m[6] && m[7]) {
      out.push(
        <a
          key={key++}
          href={m[7]}
          target="_blank"
          rel="noreferrer"
          className="text-primary underline-offset-4 hover:underline"
        >
          {m[6]}
        </a>,
      );
    }
    last = m.index + m[0].length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

const HEADING_CLS: Record<number, string> = {
  1: "mt-6 mb-3 font-serif text-3xl tracking-tight text-foreground",
  2: "mt-6 mb-3 font-serif text-2xl tracking-tight text-foreground",
  3: "mt-5 mb-2 text-lg font-semibold text-foreground",
  4: "mt-4 mb-2 text-base font-semibold text-foreground",
  5: "mt-3 mb-1.5 text-sm font-semibold text-foreground",
  6: "mt-3 mb-1.5 text-sm font-medium text-muted-foreground uppercase tracking-wide",
};

export function Markdown({
  text,
  className,
}: {
  text: string;
  className?: string;
}) {
  const lines = text.replace(/\r\n/g, "\n").split("\n");
  const blocks: ReactNode[] = [];
  let i = 0;
  let key = 0;

  const isUL = (l: string) => /^\s*[-*+]\s+/.test(l);
  const isOL = (l: string) => /^\s*\d+\.\s+/.test(l);
  const isHeading = (l: string) => /^#{1,6}\s+/.test(l);
  const isHR = (l: string) => /^\s*(---+|\*\*\*+|___+)\s*$/.test(l);
  const isQuote = (l: string) => /^\s*>\s?/.test(l);

  while (i < lines.length) {
    const line = lines[i];

    if (line.trim() === "") {
      i++;
      continue;
    }

    if (isHR(line)) {
      blocks.push(<hr key={key++} className="my-6 border-border/50" />);
      i++;
      continue;
    }

    if (isHeading(line)) {
      const m = line.match(/^(#{1,6})\s+(.*)$/)!;
      const level = m[1].length;
      const cls = HEADING_CLS[level];
      const Tag = (`h${Math.min(level + 1, 6)}` as unknown) as
        | "h2"
        | "h3"
        | "h4"
        | "h5"
        | "h6";
      blocks.push(
        <Tag key={key++} className={cls}>
          {renderInline(m[2])}
        </Tag>,
      );
      i++;
      continue;
    }

    if (isUL(line)) {
      const items: string[] = [];
      while (i < lines.length && isUL(lines[i])) {
        items.push(lines[i].replace(/^\s*[-*+]\s+/, ""));
        i++;
      }
      blocks.push(
        <ul
          key={key++}
          className="my-3 list-disc space-y-1.5 pl-5 marker:text-primary/70"
        >
          {items.map((it, j) => (
            <li key={j} className="leading-[1.7]">
              {renderInline(it)}
            </li>
          ))}
        </ul>,
      );
      continue;
    }

    if (isOL(line)) {
      const items: string[] = [];
      while (i < lines.length && isOL(lines[i])) {
        items.push(lines[i].replace(/^\s*\d+\.\s+/, ""));
        i++;
      }
      blocks.push(
        <ol
          key={key++}
          className="my-3 list-decimal space-y-1.5 pl-5 marker:text-primary/70"
        >
          {items.map((it, j) => (
            <li key={j} className="leading-[1.7]">
              {renderInline(it)}
            </li>
          ))}
        </ol>,
      );
      continue;
    }

    if (isQuote(line)) {
      const items: string[] = [];
      while (i < lines.length && isQuote(lines[i])) {
        items.push(lines[i].replace(/^\s*>\s?/, ""));
        i++;
      }
      blocks.push(
        <blockquote
          key={key++}
          className="my-3 border-l-2 border-primary/50 pl-4 text-muted-foreground italic"
        >
          {renderInline(items.join(" "))}
        </blockquote>,
      );
      continue;
    }

    const paraLines: string[] = [];
    while (
      i < lines.length &&
      lines[i].trim() !== "" &&
      !isHR(lines[i]) &&
      !isHeading(lines[i]) &&
      !isUL(lines[i]) &&
      !isOL(lines[i]) &&
      !isQuote(lines[i])
    ) {
      paraLines.push(lines[i]);
      i++;
    }
    if (paraLines.length) {
      blocks.push(
        <p key={key++} className="my-3 leading-[1.75]">
          {paraLines.map((pl, j) => (
            <Fragment key={j}>
              {j > 0 ? <br /> : null}
              {renderInline(pl)}
            </Fragment>
          ))}
        </p>,
      );
    }
  }

  return (
    <div className={cn("text-[15px] text-foreground/95", className)}>
      {blocks}
    </div>
  );
}
